// ADR-012: wire contract only. Data owns metadata authorization, canonicalization and execution.
import { success, Uuid } from "@secretaa/http-contract";
import { z } from "zod";
import { Literal } from "./expression.ts";

export const PROJECT_QUERY_LIMITS = {
  bodyBytes: 65_536, jsonDepth: 16, predicateDepth: 16, predicateNodes: 256,
  paths: 4, hops: 2, select: 20, rootIds: 100, page: 100,
} as const;
const Id = Uuid.transform(id => id.toLowerCase());
const Alias = z.string().regex(/^[a-z][a-z0-9_]{0,31}$/);
const Hash = z.string().regex(/^[a-f0-9]{64}$/);
export const QueryFieldRef = z.object({ pathAlias: Alias, fieldId: Id }).strict();
export type QueryFieldRef = z.infer<typeof QueryFieldRef>;
export const QueryPath = z.object({ alias: Alias.refine(a => a !== "root"), fromAlias: Alias,
  relationFieldId: Id, direction: z.enum(["forward", "reverse"]) }).strict();
export type QueryPath = z.infer<typeof QueryPath>;
export type QueryPredicate =
  | { op: "and" | "or"; args: QueryPredicate[] }
  | { op: "not"; arg: QueryPredicate }
  | { op: "is_null"; arg: QueryFieldRef }
  | { op: "exists"; pathAlias: string; where: QueryPredicate }
  | { op: "eq" | "ne" | "lt" | "lte" | "gt" | "gte" | "starts_with" | "contains" | "in"; left: QueryFieldRef; right: { literal: z.infer<typeof Literal> } };
export const QueryPredicate: z.ZodType<QueryPredicate> = z.lazy(() => z.discriminatedUnion("op", [
  z.object({ op: z.enum(["and", "or"]), args: z.array(QueryPredicate).min(1).max(50) }).strict(),
  z.object({ op: z.literal("not"), arg: QueryPredicate }).strict(),
  z.object({ op: z.literal("is_null"), arg: QueryFieldRef }).strict(),
  z.object({ op: z.literal("exists"), pathAlias: Alias, where: QueryPredicate }).strict(),
  z.object({ op: z.enum(["eq", "ne", "lt", "lte", "gt", "gte", "starts_with", "contains", "in"]), left: QueryFieldRef,
    right: z.object({ literal: Literal }).strict() }).strict(),
]));

/** Schema supports OpenAPI; untrusted input MUST enter through parseProjectQueryRequest before recursive parsing. */
export const ProjectQueryRequest = z.object({
  requestVersion: z.literal(1), manifestHash: Hash, rootCollectionId: Id,
  rootRecordIds: z.array(Id).min(1).max(100).optional(), scopeRootId: Id.optional(),
  paths: z.array(QueryPath).max(4).default([]),
  select: z.array(QueryFieldRef.extend({ as: Alias }).strict()).min(1).max(20),
  where: QueryPredicate.nullable().default(null),
  sort: z.object({ by: z.union([z.object({ system: z.literal("createdAt") }).strict(), QueryFieldRef]), dir: z.enum(["asc", "desc"]) }).strict()
    .default({ by: { system: "createdAt" }, dir: "desc" }),
  page: z.object({ limit: z.number().int().min(1).max(100).default(25), cursor: z.string().min(1).max(8192).optional() }).strict().default({ limit: 25 }),
}).strict();
export type ProjectQueryRequest = z.infer<typeof ProjectQueryRequest>;

/** Safe errors contain only reason + input position, never values, schema names or Zod messages. */
export class ProjectQueryValidationError extends Error {
  constructor(readonly reason: "QUERY_INVALID" | "QUERY_LIMIT_EXCEEDED" | "QUERY_FIELD_NOT_INDEXED" | "QUERY_RESOURCE_INACTIVE" | "QUERY_MANIFEST_CHANGED" | "collection_policy", readonly path = "") {
    super(reason);
  }
}
export function parseProjectQueryRequest(input: unknown): ProjectQueryRequest {
  // Iterative before JSON.stringify / Zod recursion: deeply nested or cyclic inputs cannot exhaust the stack.
  const pending: { value: unknown; depth: number; leave?: boolean }[] = [{ value: input, depth: 0 }];
  const seen = new WeakSet<object>();
  let nodes = 0;
  while (pending.length) {
    const { value, depth, leave } = pending.pop()!;
    if (leave && value !== null && typeof value === "object") { seen.delete(value); continue; }
    if (++nodes > 4096 || depth > PROJECT_QUERY_LIMITS.jsonDepth) throw new ProjectQueryValidationError("QUERY_LIMIT_EXCEEDED");
    if (value !== null && typeof value === "object") {
      if (seen.has(value) || (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null)) throw new ProjectQueryValidationError("QUERY_INVALID");
      seen.add(value);
      pending.push({ value, depth, leave: true });
      for (const child of Object.values(value)) pending.push({ value: child, depth: depth + 1 });
    } else if (typeof value === "string") {
      if (value.length > PROJECT_QUERY_LIMITS.bodyBytes) throw new ProjectQueryValidationError("QUERY_LIMIT_EXCEEDED");
    } else if (value !== null && value !== undefined && typeof value !== "boolean" && (typeof value !== "number" || !Number.isSafeInteger(value))) throw new ProjectQueryValidationError("QUERY_INVALID");
  }
  const json = JSON.stringify(input);
  if (json === undefined) throw new ProjectQueryValidationError("QUERY_INVALID");
  if (new TextEncoder().encode(json).length > PROJECT_QUERY_LIMITS.bodyBytes) throw new ProjectQueryValidationError("QUERY_LIMIT_EXCEEDED");
  const result = ProjectQueryRequest.safeParse(input);
  if (!result.success) throw new ProjectQueryValidationError("QUERY_INVALID", result.error.issues[0]?.path.join(".") ?? "");
  const q = result.data;
  const paths = new Map(q.paths.map(p => [p.alias, p]));
  if (paths.size !== q.paths.length) throw new ProjectQueryValidationError("QUERY_INVALID", "paths");
  for (const p of q.paths) {
    let alias = p.alias;
    const visited = new Set<string>();
    while (alias !== "root") {
      const node = paths.get(alias);
      if (!node || visited.has(alias)) throw new ProjectQueryValidationError("QUERY_INVALID", "paths");
      visited.add(alias); alias = node.fromAlias;
      if (visited.size > 2) throw new ProjectQueryValidationError("QUERY_LIMIT_EXCEEDED", "paths");
    }
  }
  const scalarRef = (ref: QueryFieldRef, current: string, path: string) => {
    let alias = ref.pathAlias;
    while (alias !== current) {
      const hop = paths.get(alias);
      if (!hop || hop.direction !== "forward") throw new ProjectQueryValidationError("QUERY_INVALID", path);
      alias = hop.fromAlias;
    }
  };
  const selections = new Set<string>();
  for (const [i, s] of q.select.entries()) {
    if ((s.pathAlias !== "root" && !paths.has(s.pathAlias)) || selections.has(`${s.pathAlias}:${s.as}`)) throw new ProjectQueryValidationError("QUERY_INVALID", `select.${i}`);
    selections.add(`${s.pathAlias}:${s.as}`);
  }
  if (!("system" in q.sort.by)) scalarRef(q.sort.by, "root", "sort.by");
  let predicateNodes = 0;
  const walk = (p: QueryPredicate, current: string, depth: number, path: string): void => {
    if (++predicateNodes > 256 || depth > 16) throw new ProjectQueryValidationError("QUERY_LIMIT_EXCEEDED", path);
    if (p.op === "and" || p.op === "or") p.args.forEach((a, i) => walk(a, current, depth + 1, `${path}.args.${i}`));
    else if (p.op === "not") walk(p.arg, current, depth + 1, `${path}.arg`);
    else if (p.op === "exists") {
      if (paths.get(p.pathAlias)?.fromAlias !== current) throw new ProjectQueryValidationError("QUERY_INVALID", `${path}.pathAlias`);
      walk(p.where, p.pathAlias, depth + 1, `${path}.where`);
    } else if (p.op === "is_null") scalarRef(p.arg, current, `${path}.arg`);
    else {
      if (!("left" in p)) throw new ProjectQueryValidationError("QUERY_INVALID", path);
      scalarRef(p.left, current, `${path}.left`);
      const literal = p.right.literal;
      if (literal === null || (Array.isArray(literal) !== (p.op === "in")) || (Array.isArray(literal) && literal.some(v => v === null))) throw new ProjectQueryValidationError("QUERY_INVALID", `${path}.right`);
    }
  };
  if (q.where) walk(q.where, "root", 1, "where");
  return q;
}

export const QueryValue = z.discriminatedUnion("type", [
  z.object({ type: z.enum(["text", "date", "number", "select"]), value: z.string() }).strict(),
  z.object({ type: z.literal("bool"), value: z.boolean() }).strict(),
  z.object({ type: z.literal("money"), minor: z.string().regex(/^-?\d{1,18}$/), currency: z.string().regex(/^[A-Z]{3}$/), scale: z.number().int().min(0).max(4) }).strict(),
]);
const Source = z.object({ collectionId: Id, recordId: Id, recordVersion: z.number().int().positive() }).strict();
export const QueryCell = z.object({ as: Alias, value: QueryValue.nullable(), source: Source.extend({ fieldId: Id }).nullable() }).strict()
  .refine(c => (c.value === null) === (c.source === null), "SOURCE_VALUE_PAIR_REQUIRED");
export interface QueryNode { source: z.infer<typeof Source>; cells: z.infer<typeof QueryCell>[]; relations: Record<string, QueryNode | null | QueryNode[]> }
export const QueryNode: z.ZodType<QueryNode> = z.lazy(() => z.object({ source: Source, cells: z.array(QueryCell).max(20),
  relations: z.record(Alias, z.union([QueryNode, z.null(), z.array(QueryNode).max(20)])),
}).strict().refine(n => n.cells.every(c => !c.source || (c.source.collectionId === n.source.collectionId && c.source.recordId === n.source.recordId && c.source.recordVersion === n.source.recordVersion)), "CELL_RECORD_SOURCE_MISMATCH"));
export const ProjectQueryResult = z.object({ manifestHash: Hash, rows: z.array(QueryNode).max(100), nextCursor: z.string().max(8192).nullable() }).strict();
export const ProjectQueryResponse = success(ProjectQueryResult);
export type ProjectQueryResult = z.infer<typeof ProjectQueryResult>;
